import { format, parseISO } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import pt from 'date-fns/locale/pt';

export type LanguageCode = 'en' | 'es' | 'pt';

const localeMap = { es, en: enUS, pt };

/**
 * Formats next payment date: "15 de May. del 2025" (es), "May 15, 2025" (en), etc.
 * Uses abbreviated month; Spanish uses "de" and "del" around month and year.
 */
export function formatNextPaymentDate(
  dateOrIso: Date | string,
  languageCode: LanguageCode,
): string {
  const date = typeof dateOrIso === 'string' ? parseISO(dateOrIso) : dateOrIso;
  const locale = localeMap[languageCode];
  if (languageCode === 'es') {
    return format(date, "d 'de' MMM. 'del' y", { locale });
  }
  if (languageCode === 'pt') {
    return format(date, "d 'de' MMM. 'de' y", { locale });
  }
  return format(date, 'MMM d, y', { locale });
}

/**
 * Formats a single date for receipt range: "31 mar 2025" or "14 abril 2025".
 * Uses abbreviated month for short form, full month for end date in Spanish design.
 */
function formatReceiptDatePart(
  dateOrIso: Date | string,
  languageCode: LanguageCode,
  useShortMonth: boolean,
): string {
  const date = typeof dateOrIso === 'string' ? parseISO(dateOrIso) : dateOrIso;
  const locale = localeMap[languageCode];
  const pattern = useShortMonth ? 'd MMM yyyy' : 'd MMMM yyyy';
  return format(date, pattern, { locale });
}

/**
 * Formats receipt date range: "31 mar 2025 - 14 abril 2025" (design format).
 */
export function formatReceiptDateRange(
  startDateIso: string,
  endDateIso: string,
  languageCode: LanguageCode,
): string {
  const start = formatReceiptDatePart(startDateIso, languageCode, true);
  const end = formatReceiptDatePart(endDateIso, languageCode, false);
  return `${start} - ${end}`;
}

/**
 * Params for modal title interpolation: "Recibo: Período {n} del {start} al {end} de {month} del {year}".
 */
export function getReceiptModalTitleParams(
  startDateIso: string,
  endDateIso: string,
  periodNumber: number,
  languageCode: LanguageCode,
): {
  periodNumber: number;
  start: string;
  end: string;
  month: string;
  year: string;
} {
  const startDate = parseISO(startDateIso);
  const endDate = parseISO(endDateIso);
  const locale = localeMap[languageCode];
  return {
    periodNumber,
    start: format(startDate, 'd', { locale }),
    end: format(endDate, 'd', { locale }),
    month: format(endDate, 'MMMM', { locale }),
    year: format(endDate, 'y', { locale }),
  };
}
